import { useRef, useState } from "react";
import { z } from "zod";
import {
  readNativeDocumentFile,
  importedLegacyFile,
  requiresNativeCodeTrust,
  type NativeFileState,
} from "./compat/native-document-file";
import { migrateLegacyDocument } from "./compat/migrate-legacy-document";
import { EngineWorkbench } from "./EngineWorkbench";
import {measureMedia} from './compat/measure-media';

const inventory = z.object({
  assets: z.record(
    z.object({ type: z.string(), remoteUrl: z.string().nullable() }),
  ),
});

/** Manual DEV-only file import. No authentication, upload, save or credentials are read. */
export function LocalExportAudit() {
  const [native, setNative] = useState<NativeFileState | null>(null);
  const [pendingCode, setPendingCode] = useState<NativeFileState | null>(null);
  const [message, setMessage] = useState(
    "내보낸 JSON 사본을 선택하세요. 파일을 서버에 업로드하지 않으며 편집 결과도 저장하지 않습니다.",
  );
  const [busy, setBusy] = useState(false);
  const [pasted,setPasted]=useState('');
  const request = useRef(0);
  async function open(file: File) {
    const revision = ++request.current;
    setBusy(true);
    setPendingCode(null);
    setMessage("원본 에셋의 크기를 확인하고 있습니다.");
    try {
      if (file.size > 25 * 1024 * 1024)
        throw new Error("검증 파일은 25MB 이하로 선택해 주세요.");
      const raw: unknown = JSON.parse(await file.text());
      const existing = readNativeDocumentFile(raw);
      if (existing.status === "error") throw new Error(existing.message);
      if (existing.status === "ready") {
        if (revision === request.current) {
          if (requiresNativeCodeTrust(existing.value))
            setPendingCode(existing.value);
          else setNative(existing.value);
        }
        return;
      }
      const { assets } = inventory.parse(raw);
      const entries = Object.entries(assets).filter(
        ([, asset]) => asset.type === "image" || asset.type === "video",
      );
      const measured: Record<string, { width: number; height: number }> = {};
      let next = 0;
      await Promise.all(
        Array.from({ length: 2 }, async () => {
          while (next < entries.length) {
            const [id, asset] = entries[next++]!;
            measured[id] = await measureMedia(
              asset.type,
              asset.remoteUrl ?? "",
            );
          }
        }),
      );
      const result = migrateLegacyDocument(raw, measured);
      if (!result.ok) throw new Error(result.reason);
      if (revision !== request.current) return;
      const imported = importedLegacyFile(
        result.document,
        raw,
        new Date().toISOString(),
      );
      if (requiresNativeCodeTrust(imported)) setPendingCode(imported);
      else setNative(imported);
    } catch (error) {
      if (revision === request.current)
        setMessage(
          error instanceof Error
            ? error.message
            : "사본을 확인하지 못했습니다. 원본은 바뀌지 않았습니다.",
        );
    } finally {
      if (revision === request.current) setBusy(false);
    }
  }
  if (pendingCode)
    return (
      <main className="flex min-h-screen flex-col items-center justify-center gap-4 p-8">
        <h1>이 편집 파일에는 실행할 코드가 있습니다</h1>
        <p>
          열면 파일의 코드가 이 검증 화면에서 실행됩니다. 출처를 신뢰하는 파일만
          열어 주세요. 서버 저장·인증 연결은 하지 않습니다.
        </p>
        <button
          type="button"
          onClick={() => {
            setNative(pendingCode);
            setPendingCode(null);
          }}
        >
          이 파일의 코드를 신뢰하고 열기
        </button>
        <button
          type="button"
          onClick={() => {
            setPendingCode(null);
            setMessage("파일을 실행하지 않았습니다. 원본 파일은 그대로입니다.");
          }}
        >
          실행하지 않고 돌아가기
        </button>
      </main>
    );
  return native ? (
    <EngineWorkbench nativeFile={native} saveWork={async(file,intent)=>({ok:true,file,revision:file.savedAt,...(intent==='browser-render'?{output:{ok:true,message:'로컬 QA 사본을 출력합니다.',browser:{}}}:{})})} />
  ) : (
    <main className="flex min-h-screen flex-col items-center justify-center gap-4 p-8">
      <h1>회사판 에디터 — 로컬 사본 승계 검증</h1>
      <p role="status">{message}</p>
      <label>
        내보낸 작업 파일{" "}
        <input
          type="file"
          accept=".json,application/json"
          disabled={busy}
          onChange={(event) => {
            const file = event.currentTarget.files?.[0];
            event.currentTarget.value = "";
            if (file) void open(file);
          }}
        />
      </label>
      <label className="w-full max-w-2xl">
        복사한 편집 파일 내용
        <textarea aria-label="복사한 편집 파일 내용" value={pasted} disabled={busy}
          onChange={event=>setPasted(event.target.value)} rows={5} className="block w-full rounded border p-2"/>
      </label>
      <button type="button" disabled={busy||!pasted.trim()} onClick={()=>void open(new File([pasted],'copied-editor.json',{type:'application/json'}))}>
        붙여넣은 편집 파일 불러오기
      </button>
      <p>
        미사용 라이브러리는 내보내기에서 빠졌을 수 있습니다. 이 경로는 전체 저장
        연동 검증을 대신하지 않습니다.
      </p>
    </main>
  );
}
